import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import Button from '../components/Button';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { formatDate } from '../lib/utils';
import ImprovedCreateProjectModal from '../components/ImprovedCreateProjectModal';

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/control_panel', {
        params: { search: searchQuery, limit: 50 }
      });
      setProjects(response.data.data || []);
    } catch (error) {
      toast.error('Ошибка загрузки проектов');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [searchQuery]);

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот проект?')) return;

    try {
      await api.delete(`/control_panel/${id}`);
      toast.success('Проект удален');
      fetchProjects();
    } catch (error) {
      toast.error('Ошибка удаления проекта');
    }
  };

  const handleCreateProject = async (data) => {
    try {
      const response = await api.post('/control_panel', data);
      toast.success('Проект создан');
      setShowCreateModal(false);
      navigate(`/projects/${response.data.ID}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка создания проекта');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Мои проекты</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Создать проект
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Поиск проектов..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Проектов пока нет. Создайте первый проект!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Card key={project.ID} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{project.Script_Name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  <p>Создан: {formatDate(project.Created_At)}</p>
                  <p>Обновлен: {formatDate(project.Updated_At)}</p>
                </div>
                
                {project.Have_Tags && project.Have_Tags.length > 0 && (
                  <div>
                    <p className="text-xs font-medium mb-1">Теги (есть):</p>
                    <div className="flex flex-wrap gap-1">
                      {project.Have_Tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                      {project.Have_Tags.length > 3 && (
                        <span className="text-xs text-muted-foreground">+{project.Have_Tags.length - 3}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`/projects/${project.ID}`)}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Редактировать
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(project.ID)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showCreateModal && (
        <ImprovedCreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateProject}
        />
      )}
    </div>
  );
}
